import flow from 'lodash/flow';
import React from 'react';

import { lazy } from '@components/common/routeLoading';
import { stores } from '@stores/index';
import { with404Redirect } from '@utils/hoc/with404Redirect';
import { withDataGraphAudienceRedirect } from '@utils/hoc/withDataGraphAudienceRedirect';

import { Path } from './constants';

/**
 * Lazily-loaded page components for the authenticated routes.
 *
 * Split out of ./authorizedRoutes so both stay under the `max-lines` limit.
 * Chunk names are preserved from the original route table so the build output
 * does not shift.
 */

export const Connections = lazy(() =>
  import(/* webpackChunkName: "connections" */ '@components/connections').then((module) => ({
    default: module.Connections,
  })),
);

export const Canny = lazy(() => import(/* webpackChunkName: "auth.canny" */ '@auth/canny'));

export const GetStarted = lazy(
  () => import(/* webpackChunkName: "quickstart" */ '@components/quickstart'),
);

export const InlineGetStarted = lazy(() =>
  import(
    /* webpackChunkName: "getStarted.inline" */ '@components/getStarted/views/getStartedPage'
  ).then((module) => ({
    default: module.GetStartedPage,
  })),
);

export const Directory = lazy(() =>
  import(/* webpackChunkName: "directory" */ '@components/directory').then((module) => ({
    default: module.Directory,
  })),
);

export const Profile = lazy(() =>
  import(/* webpackChunkName: "profile" */ '@components/profile/profile').then((module) => ({
    default: module.Profile,
  })),
);

export const Workspace = lazy(
  () => import(/* webpackChunkName: "workspace" */ '@components/profile/workspace'),
);

export const Organization = lazy(
  () => import(/* webpackChunkName: "organization" */ '@components/profile/organization'),
);

export const Invoices = lazy(
  () => import(/* webpackChunkName: "invoices" */ '@components/profile/invoices'),
);

// --- Sources ----------------------------------------------------------------

export const SourcesList = lazy(() =>
  import(/* webpackChunkName: "sources.source-list" */ '@components/sources/source-list').then(
    (module) => ({
      default: module.SourcesList,
    }),
  ),
);

const SourceViewPage = lazy(() =>
  import(/* webpackChunkName: "sources.source-view" */ '@components/sources/source-view').then(
    (module) => ({
      default: module.SourceView,
    }),
  ),
);

/**
 * `SourceView` also backs the model and audience detail routes, whose URLs carry
 * rETL source ids — so it is exported unwrapped for those.
 */
export const SourceView = SourceViewPage;

/**
 * The source detail route redirects data-graph-audience sources to their own
 * section, and unknown ids back to the list. Both HOCs resolve stores through
 * `useStores()` internally; only the `exists` probe needs the root store, and
 * `stores` is the same singleton that `useStores()` returns from context.
 */
export const SourceDetails = flow(withDataGraphAudienceRedirect, (component: React.ComponentType) =>
  with404Redirect(component, {
    exists: (id) => !!stores.sourcesListStore.sourceById(id),
    redirectPath: Path.SOURCES,
  }),
)(SourceViewPage);

export const CreateSource = lazy(() =>
  import(
    /* webpackChunkName: "workflows.source.create" */ '@components/workflows/source/createSource'
  ).then((module) => ({
    default: module.CreateSource,
  })),
);

export const EventsView = lazy(
  () => import(/* webpackChunkName: "sources.liveEvents" */ '@components/sources/liveEvents'),
);

export const ConnectExistingDestination = lazy(
  () =>
    import(
      /* webpackChunkName: "workflows.source.connectExistingDestination" */ '@components/workflows/source/connectExistingDestination'
    ),
);

export const ConnectNewDestination = lazy(
  () =>
    import(
      /* webpackChunkName: "workflows.connectNewDestination.trackingPlanList" */ '@components/workflows/source/connectNewDestination'
    ),
);

// --- Destinations -----------------------------------------------------------

export const DestinationsList = lazy(() =>
  import(
    /* webpackChunkName: "destinations.destination-list" */ '@components/destinations/destination-list'
  ).then((module) => ({
    default: module.DestinationsList,
  })),
);

const DestinationDetailsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "destinations.destination-view" */ '@components/destinations/destination-view'
    ),
);

export const DestinationDetails = with404Redirect(DestinationDetailsPage, {
  exists: (id) => !!stores.destinationsListStore.destinationById(id),
  redirectPath: Path.DESTINATIONS,
});

export const CreateDestination = lazy(
  () =>
    import(
      /* webpackChunkName: "workflows.destination.create" */ '@components/workflows/destination/create'
    ),
);

export const CloneDestination = lazy(
  () =>
    import(
      /* webpackChunkName: "workflows.destination.clone" */ '@components/workflows/destination/clone/clone'
    ),
);

export const StatusesView = lazy(
  () =>
    import(/* webpackChunkName: "destinations.liveEvents" */ '@components/destinations/liveEvents'),
);

export const DestinationMappingConfig = lazy(
  () =>
    import(
      /* webpackChunkName: "destinations.destination-view.configurationV2.mappingComponents.setUpMapping" */ '@components/destinations/destination-view/configurationV2/mappingComponents'
    ),
);

export const ConnectNewSource = lazy(
  () =>
    import(
      /* webpackChunkName: "workflows.destination.create.connectNewSource" */ '@components/workflows/destination/connectNewSource'
    ),
);

export const ConnectExistingSource = lazy(
  () =>
    import(
      /* webpackChunkName: "workflows.destination.connectExistingSource" */ '@components/workflows/destination/connectExistingSource'
    ),
);

// --- Transformations and libraries ------------------------------------------

export const TransformationsPage = lazy(() =>
  import(/* webpackChunkName: "transformations" */ '@components/transformations').then(
    (module) => ({ default: module.TransformationsV2Page }),
  ),
);

export const TransformationDetailsV2 = lazy(() =>
  import(
    /* webpackChunkName: "connections" */ '@components/transformationsV2/transformations/components/transformationDetails'
  ).then((module) => ({
    default: module.TransformationDetailsPage,
  })),
);

export const EventTransformStatusesView = lazy(
  () =>
    import(
      /* webpackChunkName: "transformations.liveEvents" */ '@components/transformations/liveEvents'
    ),
);

export const LibraryDetailsV2 = lazy(() =>
  import(
    /* webpackChunkName: "transformations.libraries.libraryDetails" */ '@components/transformations/libraries/libraryDetails'
  ).then((module) => ({ default: module.LibraryDetailsV2 })),
);

// --- Models, audiences, syncs ----------------------------------------------

export const ModelsList = lazy(() =>
  import(/* webpackChunkName: "models.modelsList" */ '@components/models/modelsList').then(
    (module) => ({
      default: module.ModelsList,
    }),
  ),
);

export const Audiences = lazy(() =>
  import(
    /* webpackChunkName: "audiences.audiencesList" */ '@components/audiences/audienceList'
  ).then((module) => ({
    default: module.AudienceList,
  })),
);

export const WarehouseSyncs = lazy(() =>
  import(/* webpackChunkName: "syncs" */ '@components/syncs').then((module) => ({
    default: module.WarehouseSyncs,
  })),
);

/**
 * The sync detail page declares its MobX stores as required props, but they are
 * supplied at runtime by `@inject` rather than by the caller. A route element
 * passes no props, so the public type is narrowed to that — the previous route
 * table erased the same mismatch implicitly by widening the component union.
 */
export const SyncDetail = lazy(
  () => import(/* webpackChunkName: "syncs.syncDetails" */ '@components/syncs/syncDetail'),
) as unknown as React.ComponentType;

// --- Tracking plans ---------------------------------------------------------

export const ChooseTrackingPlan = lazy(
  () =>
    import(
      /* webpackChunkName: "trackingPlans.createTrackingPlans.chooseTrackingPlan" */ '@components/trackingPlans/createTrackingPlans/chooseTrackingPlan'
    ),
);

export const CreateTrackingPlans = lazy(
  () =>
    import(
      /* webpackChunkName: "trackingPlans.createTrackingPlans" */ '@components/trackingPlans/createTrackingPlans'
    ),
);

export const LinkSourceComponent = lazy(
  () =>
    import(
      /* webpackChunkName: "trackingPlans.linkSource" */ '@components/trackingPlans/linkSource'
    ),
);

export const LinkTrackingPlanComponent = lazy(
  () =>
    import(
      /* webpackChunkName: "trackingPlans.linkTrackingPlan" */ '@components/trackingPlans/linkTrackingPlan'
    ),
);

// --- Everything else --------------------------------------------------------

export const HealthDashboard = lazy(
  () => import(/* webpackChunkName: "eventMetrics.eventMetrics" */ '@components/healthDashboard'),
);

export const DataCatalog = lazy(
  () => import(/* webpackChunkName: "dataCatalog" */ '@components/dataCatalog'),
);

export const RetlConnectionDetails = lazy(() =>
  import(
    /* webpackChunkName: "retlWorkflow.connections" */ '@components/retlWorkflow/components/connection'
  ).then((module) => ({
    default: module.ConnectionView,
  })),
);

export const DataGraph = lazy(() =>
  import(/* webpackChunkName: "dataGraph" */ '@components/dataGraph').then((module) => ({
    default: module.DataGraph,
  })),
);

export const TrialExpiredPage = lazy(() =>
  import(/* webpackChunkName: "trialExpired" */ '@components/trial/pages/trialExpired').then(
    (module) => ({
      default: module.TrialExpiredPage,
    }),
  ),
);
